import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import LoadingSpinner from './components/LoadingSpinner';
import SubmitComplaintModal from './components/SubmitComplaintModal';
import ComplaintDetailModal from './components/ComplaintDetailModal';
import NoAccessNotice from './components/NoAccessNotice';

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_FLOW = [
  { key: 'draft',                label: 'Draft',              color: colors.textDim },
  { key: 'submitted',            label: 'Submitted',          color: '#60a5fa' },
  { key: 'acknowledged',         label: 'Acknowledged',       color: '#a78bfa' },
  { key: 'initial_report',       label: 'Initial Report',     color: '#f472b6' },
  { key: 'fast_close_pending',   label: 'Fast Close',         color: colors.statusYellow },
  { key: 'fast_close_confirmed', label: 'Payment Confirmed',  color: '#86efac' },
  { key: '8d_report',            label: '8D Report',          color: '#fb923c' },
  { key: 'corrective_action',    label: 'Corrective Action',  color: colors.statusYellow },
  { key: 'monitoring',           label: 'Monitoring',         color: colors.primary },
  { key: 'closed',               label: 'Closed',             color: colors.success },
  { key: 'archived',             label: 'Archived',           color: colors.textDim },
];

const SUPPLIER_LABELS = {
  gpv: 'GPV', ubiqconn: 'Ubiqconn', acmepoint: 'Acmepoint', hk_ringer: 'HK Ringer',
};

const ESCALATION_COLORS = { 1: colors.success, 2: colors.statusYellow, 3: colors.danger };

// Derives who is currently blocking progress
export function getWaitingFor(c) {
  const { status, eightDReport, correctiveAction, eightDReview, caReview, infoRequests, faultChallenge } = c;
  const hasOpenInfoRequest = Array.isArray(infoRequests) && infoRequests.some(r => r.status === 'open');
  // A finished case is waiting on nobody — it used to fall through to 'nin' and
  // sit in the action count forever.
  if (status === 'closed' || status === 'archived') return 'done';
  if (status === 'submitted') return 'gpv';
  if (status === 'acknowledged' && hasOpenInfoRequest) return 'nin';
  if (status === 'acknowledged') return 'gpv';
  if (status === 'initial_report' && faultChallenge?.status === 'challenged') return 'gpv';
  if (status === 'initial_report' && faultChallenge?.status === 'supplier_responded') return 'nin';
  if (status === 'initial_report') return 'nin';
  if (status === 'fast_close_pending' && c.fastClose?.gpvDeclinedAt) return 'nin';
  if (status === 'fast_close_pending') return 'gpv';
  if (status === 'fast_close_confirmed') return 'nin';
  if (status === '8d_report' && !eightDReport?.submittedAt) return 'gpv';
  if (status === '8d_report' && eightDReview?.status === 'needs_more') return 'gpv';
  if (status === '8d_report') return 'nin';
  if (status === 'corrective_action' && !correctiveAction?.submittedAt) return 'gpv';
  if (status === 'corrective_action' && caReview?.status === 'challenged') return 'gpv';
  if (status === 'corrective_action') return 'nin';
  if (status === 'monitoring') {
    const lots = c.monitoring?.lots || [];
    const total = c.monitoring?.totalLots || 0;
    if (lots.some(l => l.failureAction?.status === 'responded')) return 'nin';
    if (lots.some(l => l.failureAction?.status === 'requested')) return 'gpv';
    // Unverified lots need NI; a complete run needs NI to close it. Otherwise
    // the supplier is simply working through the schedule — flagging NI for
    // months made the action badge meaningless.
    if (lots.some(l => !l.verifiedAt)) return 'nin';
    if (total && lots.length >= total) return 'nin';
    return 'gpv';
  }
  // Payment receipt pending (8D path)
  if (c.compensation?.status === 'payment_sent' && !c.compensation?.paymentReceivedAt) return 'nin';
  return 'nin';
}

function elapsedLabel(date) {
  if (!date) return null;
  const ms = Date.now() - new Date(date).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 24) return h < 1 ? '<1h' : `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return `${Math.floor(d / 30)}mo`;
}

export default function ComplaintsDashboard() {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const role = user?.publicMetadata?.role || 'guest';
  const normalizedRole = String(role).toLowerCase().trim();
  const isAdmin = role === 'admin';
  const isService = normalizedRole.startsWith('service');

  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [activeComplaint, setActiveComplaint] = useState(null);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const fetchComplaints = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/complaints`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.ok) setComplaints(data.complaints || []);
    } catch (err) {
      console.error('Failed to fetch complaints:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!isAdmin && !isService) return;
    fetchComplaints();
  }, [isAdmin, isService, fetchComplaints]);

  const handleCreated = (complaint) => {
    setComplaints(prev => [complaint, ...prev]);
    setShowSubmitModal(false);
    setActiveComplaint(complaint);
  };

  const handleUpdated = (updated) => {
    setComplaints(prev => prev.map(c => c._id === updated._id ? updated : c));
    setActiveComplaint(updated);
  };

  const visibleStatuses = showArchived ? STATUS_FLOW : STATUS_FLOW.filter(s => s.key !== 'archived');

  const filtered = filterSupplier ? complaints.filter(c => c.supplier === filterSupplier) : complaints;

  const grouped = visibleStatuses.reduce((acc, { key }) => {
    acc[key] = filtered.filter(c => c.status === key);
    return acc;
  }, {});

  // Count NIN action-required items
  // Counted over the same set the columns show, so the header total always
  // matches the board when a supplier filter is applied.
  const ninActionCount = filtered.filter(c => getWaitingFor(c) === 'nin').length;

  if (!isSignedIn || (!isAdmin && !isService)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <NoAccessNotice pageName="the NCR board" />
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size={64} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '1.25rem 1.5rem 0', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        <div className="ni-brand" style={{ flex: 1, minWidth: 0 }}>
          <Link to="/" title="Back to main page" aria-label="Back to main page" style={{ display: 'inline-flex', lineHeight: 0 }}>
            <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
          </Link>
          <div className="ni-brand-copy">
            <span className="ni-eyebrow">Supplier Complaints · NCR</span>
            <span className="ni-brand-subtitle">Non-conformance reports with GPV &amp; suppliers</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {ninActionCount > 0 && (
            <div style={{ background: colors.danger, color: '#fff', borderRadius: 8, padding: '0.3rem 0.75rem', fontSize: '0.78rem', fontWeight: 700 }}>
              {ninActionCount} action{ninActionCount > 1 ? 's' : ''} required
            </div>
          )}
          <select
            value={filterSupplier}
            onChange={e => setFilterSupplier(e.target.value)}
            className="ni-input"
            style={{ fontSize: '0.82rem', padding: '0.35rem 0.6rem', minWidth: 120 }}
          >
            <option value="">All suppliers</option>
            {Object.entries(SUPPLIER_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowArchived(v => !v)}
            className={`ni-button ${showArchived ? 'ni-button--ghost' : 'ni-button--muted'}`}
            style={{ fontSize: '0.82rem' }}
          >
            {showArchived ? 'Hide archived' : 'Show archived'}
          </button>
          <Link to="/rmas" className="ni-button ni-button--ghost" style={{ fontSize: '0.82rem', textDecoration: 'none' }}>
            RMA Board
          </Link>
          <button
            type="button"
            onClick={() => setShowSubmitModal(true)}
            className="ni-button ni-button--primary"
            style={{ fontSize: '0.82rem' }}
          >
            + Submit Complaint
          </button>
        </div>
      </div>

      <div style={boardStyle}>
        {visibleStatuses.map(({ key, label, color }) => {
          const col = grouped[key] || [];
          const actionCount = col.filter(c => getWaitingFor(c) === 'nin').length;
          return (
            <div key={key} style={columnStyle}>
              <div style={{ ...columnHeader, borderTopColor: color }}>
                <span style={{ color }}>{label}</span>
                <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                  {actionCount > 0 && (
                    <span style={{ fontSize: '0.68rem', background: colors.danger, color: '#fff', borderRadius: 8, padding: '0 5px', fontWeight: 700 }}>
                      {actionCount}
                    </span>
                  )}
                  {col.length > 0 && (
                    <span style={{ fontSize: '0.68rem', background: colors.surfaceRaised, color: colors.textDim, borderRadius: 8, padding: '0 5px' }}>
                      {col.length}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {col.length === 0 ? (
                  <div style={{ color: colors.textDim, fontSize: '0.75rem', padding: '0.4rem 0', textAlign: 'center' }}>—</div>
                ) : (
                  col.map(c => (
                    <ComplaintCard
                      key={c._id}
                      complaint={c}
                      onClick={() => setActiveComplaint(c)}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showSubmitModal && (
        <SubmitComplaintModal
          onClose={() => setShowSubmitModal(false)}
          onCreated={handleCreated}
        />
      )}

      {activeComplaint && (
        <ComplaintDetailModal
          complaint={activeComplaint}
          onClose={() => setActiveComplaint(null)}
          onUpdated={handleUpdated}
        />
      )}
    </div>
  );
}

function ComplaintCard({ complaint, onClick }) {
  const { complaintId, supplier, title, escalationLevel, compensation, updatedAt, statusHistory } = complaint;
  const escalColor = ESCALATION_COLORS[escalationLevel] || colors.textMuted;
  const claimed = compensation?.totalClaimed;
  const waitingFor = getWaitingFor(complaint);
  const isNinAction = waitingFor === 'nin';

  // Time in current status
  const lastStatusEntry = statusHistory?.length ? statusHistory[statusHistory.length - 1] : null;
  const sinceStatus = elapsedLabel(lastStatusEntry?.date || updatedAt);

  return (
    <div
      onClick={onClick}
      style={{
        ...cardStyle,
        borderColor: isNinAction ? colors.danger : colors.panelBorder,
        borderWidth: isNinAction ? '1.5px' : '1px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.4rem' }}>
        <span style={{ fontWeight: 700, fontSize: '0.75rem', color: colors.textStrong }}>{complaintId}</span>
        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: escalColor }}>L{escalationLevel}</span>
      </div>

      <div style={{ fontSize: '0.8rem', color: colors.textBody, marginTop: '0.15rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
        {title}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.45rem' }}>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: colors.primary }}>
          {SUPPLIER_LABELS[supplier] || supplier}
        </span>
        {claimed > 0 && (
          <span style={{ fontSize: '0.68rem', color: colors.textDim }}>€{claimed.toLocaleString()}</span>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.35rem' }}>
        <span style={{
          fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.45rem', borderRadius: 4,
          background: isNinAction ? `${colors.danger}22` : `${colors.textDim}22`,
          color: isNinAction ? colors.danger : colors.textDim,
        }}>
          {isNinAction
            ? 'NIN action'
            : waitingFor === 'done'
              ? 'Complete'
              : `Waiting · ${SUPPLIER_LABELS[supplier] || supplier}`}
        </span>
        {sinceStatus && (
          <span style={{ fontSize: '0.65rem', color: colors.textDim }}>{sinceStatus}</span>
        )}
      </div>
    </div>
  );
}

const boardStyle = {
  display: 'flex',
  flexDirection: 'row',
  gap: '0.75rem',
  padding: '1rem 1.5rem 1.5rem',
  overflowX: 'auto',
  flex: 1,
  alignItems: 'flex-start',
};

const columnStyle = {
  minWidth: 185,
  maxWidth: 220,
  flex: '0 0 auto',
  background: colors.surfaceMuted,
  borderRadius: 10,
  padding: '0.75rem',
};

const columnHeader = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  fontWeight: 700,
  fontSize: '0.75rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderTop: '2px solid',
  paddingTop: '0.4rem',
  marginBottom: '0.75rem',
};

const cardStyle = {
  background: colors.surface,
  border: `1px solid ${colors.panelBorder}`,
  borderRadius: 8,
  padding: '0.6rem 0.7rem',
  cursor: 'pointer',
  transition: 'border-color 0.15s',
};
